import { emit } from "@tauri-apps/api/event";
import type { ActivityLabel } from "@/lib/activity-label";
import { isTauri } from "@/lib/is-tauri";

export const COMPACT_RESOURCE_LIFECYCLE_EVENT =
  "denker://compact-resource-lifecycle";
export const COMPACT_RESOURCE_THUMBNAIL_EVENT =
  "denker://compact-resource-thumbnail";
export const COMPACT_RESOURCE_BROWSER_EVENT = "denker:compact-resource";

export interface CompactResourceDescriptor {
  kind: "app" | "screenshot" | "action";
  key: string;
  /** English; `activityLabel` renders it in the app language. */
  label: string;
  activityLabel?: ActivityLabel;
  /** Desktop frames drive the live logo and pin; MCP frames name apps. */
  route?: "ax" | "mcp";
  /** A matching native receipt names this exact read's app on completion. */
  appName?: string;
}

export interface CompactResourceLifecycle {
  executionId: string;
  originDisplayId?: number;
  clientRequestId?: string;
  toolUseId?: string;
  phase: "started" | "finished" | "execution_finished" | "execution_bound" | "field_edit" | "field_glow";
  resource?: CompactResourceDescriptor;
  /** A `finished` frame of a tool call that returned an error. */
  failed?: boolean;
  /** Display name of the app a `field_edit` frame worked in; left out when
   *  the app cannot be named. */
  appLabel?: string;
  /** A native AX text attempt names the field without exposing its value. */
  fieldName?: string;
  editStatus?: "verified" | "uncertain";
  /** Exact attached pair has focus and the field is visible at this point. */
  showCue?: boolean;
  cueSequence?: number;
  bounds?: { x: number; y: number; width: number; height: number };
}

export interface CompactResourceThumbnail {
  executionId: string;
  captureId: string;
  displayId?: number | null;
  ordinal: number;
  /** Legacy producer hint. Compact execution activity is shared by all windows. */
  visibility: "all-displays" | "captured-display";
  jpegBase64: string;
}

export function shouldShowCompactThumbnail(
  _thumbnail: CompactResourceThumbnail,
  _windowDisplayId: number | null,
): boolean {
  // Compact resources describe one shared execution. `displayId` identifies
  // where a screenshot was captured; it must not make sibling display
  // webviews render different activity histories.
  return true;
}

export function publishCompactResourceLifecycle(
  detail: CompactResourceLifecycle,
): void {
  if (isTauri()) {
    void emit(COMPACT_RESOURCE_LIFECYCLE_EVENT, detail).catch(() => undefined);
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CompactResourceLifecycle>(COMPACT_RESOURCE_BROWSER_EVENT, {
      detail,
    }),
  );
}

export function publishCompactResourceThumbnail(
  thumbnail: CompactResourceThumbnail,
): void {
  if (isTauri()) {
    void emit(COMPACT_RESOURCE_THUMBNAIL_EVENT, thumbnail).catch(
      () => undefined,
    );
    return;
  }
  publishBrowserThumbnail(thumbnail);
}

function publishBrowserThumbnail(thumbnail: CompactResourceThumbnail): void {
  window.dispatchEvent(
    new CustomEvent<CompactResourceThumbnail>(
      COMPACT_RESOURCE_THUMBNAIL_EVENT,
      { detail: thumbnail },
    ),
  );
}
